import React, { useEffect, useState, useRef } from 'react';
import confetti from 'canvas-confetti';

const AnniversaryCelebration = ({ joinDate, employeeName }) => {
  const [show, setShow] = useState(false);
  const [years, setYears] = useState(0);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!joinDate) return;
    const today = new Date();
    const jd = new Date(joinDate + 'T00:00:00');
    if (isNaN(jd.getTime())) return;

    const sameMonth = today.getMonth() === jd.getMonth();
    const sameDay = today.getDate() === jd.getDate();
    const yrs = today.getFullYear() - jd.getFullYear();

    if (sameMonth && sameDay && yrs > 0) {
      setYears(yrs);
      setShow(true);
    }
  }, [joinDate]);

  useEffect(() => {
    if (!show || firedRef.current) return;
    firedRef.current = true;

    const duration = 4000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ['#ff0000', '#ffa500', '#ffff00', '#00ff00', '#0000ff', '#8b00ff', '#ff69b4'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ['#ff0000', '#ffa500', '#ffff00', '#00ff00', '#0000ff', '#8b00ff', '#ff69b4'],
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();

    // Big burst
    setTimeout(() => {
      confetti({
        particleCount: 120,
        spread: 100,
        origin: { y: 0.6 },
        colors: ['#ff0000', '#ffa500', '#ffff00', '#00ff00', '#0000ff', '#8b00ff', '#ff69b4', '#00ced1', '#ffd700'],
      });
    }, 500);
  }, [show]);

  if (!show) return null;

  const firstName = (employeeName || '').split(' ')[0];
  const ordinal = years === 1 ? '1st' : years === 2 ? '2nd' : years === 3 ? '3rd' : `${years}th`;

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-4"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      }}
      data-testid="anniversary-celebration"
    >
      {/* Animated stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${4 + Math.random() * 8}px`,
              height: `${4 + Math.random() * 8}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: ['#ffd700', '#ff69b4', '#00ced1', '#fff', '#ffa500'][i % 5],
              opacity: 0.3 + Math.random() * 0.5,
              animation: `pulse ${1.5 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative px-5 py-6 text-center text-white">
        {/* Party poppers */}
        <div className="text-4xl mb-2" style={{ animation: 'bounce 1s ease infinite' }}>
          <span style={{ display: 'inline-block', transform: 'scaleX(-1)' }}>🎉</span>
          <span className="mx-2">🎊</span>
          <span>🎉</span>
        </div>

        <h2 className="text-lg font-black tracking-wide mb-1" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          Happy {ordinal} Work Anniversary!
        </h2>
        <p className="text-sm font-medium opacity-90" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.2)' }}>
          Congratulations {firstName}! Thank you for {years} amazing {years === 1 ? 'year' : 'years'} with us
        </p>

        {/* Year badges */}
        <div className="flex justify-center gap-1.5 mt-3">
          {[...Array(Math.min(years, 10))].map((_, i) => (
            <div
              key={i}
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold"
              style={{
                background: 'rgba(255,255,255,0.25)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,0.4)',
                animation: `fadeInUp 0.5s ease forwards`,
                animationDelay: `${0.3 + i * 0.1}s`,
                opacity: 0,
              }}
            >
              {i + 1}
            </div>
          ))}
          {years > 10 && (
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[8px] font-bold"
              style={{ background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)' }}>
              +{years - 10}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.5); opacity: 0.8; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
};

export default AnniversaryCelebration;
